import React from "react";

export default function ConversationList({
  conversations = [],
  selectedPhone,
  onSelectChat,
  searchQuery,
  setSearchQuery,
  filterType,
  setFilterType,
  loading,
  onNewChat,
}) {
  // Format message time like WhatsApp Web
  const formatTime = (ts) => {
    if (!ts) return "";
    const date = new Date(ts);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  // Compute dynamic filter counts safely
  const allCount = conversations.length;
  const unreadCount = conversations.filter((c) => Number(c.unread_count) > 0).length;
  const subsCount = conversations.filter((c) => !!c.subscription_id).length;

  // Filter conversations based on search and active filter tab
  const filteredConversations = conversations.filter((c) => {
    const q = (searchQuery || "").toLowerCase().trim();
    const name = String(c.name || "").toLowerCase();
    const phone = String(c.phone || "").toLowerCase();
    const subId = String(c.subscription_id || "").toLowerCase();
    const lastMsg = String(c.last_message || "").toLowerCase();

    const matchesSearch =
      !q ||
      name.includes(q) ||
      phone.includes(q) ||
      subId.includes(q) ||
      lastMsg.includes(q);

    if (!matchesSearch) return false;

    if (filterType === "unread") return Number(c.unread_count) > 0;
    if (filterType === "subscribers") return !!c.subscription_id;
    return true;
  });

  return (
    <nav className="wa-conv-sidebar" aria-label="WhatsApp Chats">
      {/* Top Search & Actions */}
      <div className="wa-search-container">
        <div className="wa-search-box">
          <i className="bi bi-search wa-search-icon" aria-hidden="true"></i>
          <input
            type="search"
            placeholder="Search or start new chat"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setSearchQuery("");
            }}
            aria-label="Search conversations"
          />
          {searchQuery && (
            <button
              type="button"
              className="wa-search-clear"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              <i className="bi bi-x-circle-fill"></i>
            </button>
          )}
        </div>
      </div>

      {/* Filter Tabs / Pills */}
      <div className="wa-filter-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={filterType === "all"}
          className={`wa-tab-pill ${filterType === "all" ? "active" : ""}`}
          onClick={() => setFilterType("all")}
        >
          All {allCount > 0 && <span className="wa-tab-badge">{allCount}</span>}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filterType === "unread"}
          className={`wa-tab-pill ${filterType === "unread" ? "active" : ""}`}
          onClick={() => setFilterType("unread")}
        >
          Unread {unreadCount > 0 && <span className="wa-tab-badge unread">{unreadCount}</span>}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filterType === "subscribers"}
          className={`wa-tab-pill ${filterType === "subscribers" ? "active" : ""}`}
          onClick={() => setFilterType("subscribers")}
        >
          Subscribers {subsCount > 0 && <span className="wa-tab-badge gold">{subsCount}</span>}
        </button>
      </div>

      {/* Conversation List Scroll Area */}
      <div className="wa-conv-list" role="list">
        {loading && conversations.length === 0 ? (
          <div className="wa-skeleton-list">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="wa-skeleton-item">
                <div className="wa-skeleton-avatar"></div>
                <div className="wa-skeleton-content">
                  <div className="wa-skeleton-line top"></div>
                  <div className="wa-skeleton-line bottom"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="wa-empty-chat-list">
            <div className="wa-empty-icon">
              <i className="bi bi-chat-dots"></i>
            </div>
            <h6>No chats found</h6>
            <p>
              {searchQuery
                ? `No messages matching "${searchQuery}"`
                : filterType !== "all"
                ? `No chats under "${filterType}".`
                : "No customer conversations yet."}
            </p>
            {filterType !== "all" || searchQuery ? (
              <button
                type="button"
                className="wa-btn-reset-filter"
                onClick={() => {
                  setFilterType("all");
                  setSearchQuery("");
                }}
              >
                View All Chats
              </button>
            ) : (
              <button
                type="button"
                className="wa-btn-start-chat"
                onClick={onNewChat}
              >
                <i className="bi bi-plus-lg me-1"></i> Start New Chat
              </button>
            )}
          </div>
        ) : (
          filteredConversations.map((conv) => {
            const isSelected = String(conv.phone) === String(selectedPhone);
            const initial = (conv.name || "U")[0]?.toUpperCase();
            const timeDisplay = formatTime(conv.last_message_time);
            const isSub = !!conv.subscription_id;

            return (
              <div
                key={conv.phone}
                role="listitem"
                tabIndex={0}
                className={`wa-chat-item ${isSelected ? "selected" : ""} ${Number(conv.unread_count) > 0 ? "unread" : ""}`}
                onClick={() => onSelectChat(conv.phone)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectChat(conv.phone);
                  }
                }}
              >
                {/* Avatar with Subscriber Badge */}
                <div className="wa-item-avatar-wrap">
                  <div className={`wa-item-avatar ${isSub ? "is-subscriber" : ""}`}>
                    {initial}
                  </div>
                  {isSub && (
                    <span className="wa-subscriber-crown" title="Verified Vaithikasri Subscriber">
                      <i className="bi bi-patch-check-fill"></i>
                    </span>
                  )}
                </div>

                {/* Main Information */}
                <div className="wa-item-info">
                  <div className="wa-item-header">
                    <span className="wa-item-title" title={conv.name}>
                      {conv.name || `+${conv.phone}`}
                    </span>
                    <span className="wa-item-time">{timeDisplay}</span>
                  </div>

                  <div className="wa-item-snippet-row">
                    <span className="wa-item-snippet">
                      {conv.last_direction === "outbound" && (
                        <span className="wa-snippet-ticks">
                          {conv.last_status === "read" ? (
                            <i className="bi bi-check2-all wa-tick-read" title="Read"></i>
                          ) : conv.last_status === "delivered" ? (
                            <i className="bi bi-check2-all wa-tick-delivered" title="Delivered"></i>
                          ) : (
                            <i className="bi bi-check2 wa-tick-sent" title="Sent"></i>
                          )}
                        </span>
                      )}
                      {conv.last_message_type === "unsupported" ||
                      conv.last_message === "[UNSUPPORTED]" ||
                      String(conv.last_message || "").includes("Unsupported message") ||
                      String(conv.last_message || "").includes("View-Once") ||
                      String(conv.last_message || "").includes("View Once") ? (
                        <span className="wa-snippet-viewonce">
                          <span className="wa-viewonce-circle-mini">1</span>
                          <span>Photo (View once)</span>
                        </span>
                      ) : conv.last_message_type === "audio" || String(conv.last_message || "").includes("Voice Note") ? (
                        <span className="wa-snippet-media">
                          <i className="bi bi-mic-fill me-1 text-muted"></i>
                          <span>Voice message</span>
                        </span>
                      ) : conv.last_message_type === "image" ? (
                        <span className="wa-snippet-media">
                          <i className="bi bi-camera-fill me-1 text-muted"></i>
                          <span>Photo</span>
                        </span>
                      ) : conv.last_message_type === "video" ? (
                        <span className="wa-snippet-media">
                          <i className="bi bi-camera-video-fill me-1 text-muted"></i>
                          <span>Video</span>
                        </span>
                      ) : (
                        conv.last_message || "Message"
                      )}
                    </span>

                    {/* Right Indicators */}
                    <div className="wa-item-badges">
                      {isSub && conv.subscription_id && (
                        <span className="wa-sub-chip" title="Subscription ID">
                          {conv.subscription_id}
                        </span>
                      )}
                      {Number(conv.unread_count) > 0 && (
                        <span className="wa-unread-badge">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Floating / Bottom Start New Chat Button */}
      <div className="wa-sidebar-bottom-bar">
        <button
          type="button"
          className="wa-btn-new-chat-bottom"
          onClick={onNewChat}
          title="Start conversation with a new phone number"
        >
          <i className="bi bi-chat-plus-fill me-2"></i>
          <span>New Chat</span>
        </button>
      </div>
    </nav>
  );
}
